import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Wind, Activity, Shield, Award, Clock, Building2, Star, CheckCircle, ClipboardCheck, Truck, BarChart3 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StickyCtaBar from "@/components/StickyCtaBar";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import heroImage from "@/assets/hero-earthy.jpg";
import { LeafShape, BlobShape } from "@/components/OrganicShapes";

const services = [
  {
    title: "Construction Dust Monitoring",
    description: "Continuous PM10 and PM2.5 boundary monitoring with MCERTS-equivalent instrumentation for construction sites across London.",
    path: "/construction-dust-monitoring-london",
  },
  {
    title: "Real-Time Monitoring",
    description: "Live dust data with cloud dashboards and automated SMS/email alerts when action levels are approached or exceeded.",
    path: "/real-time-dust-monitoring",
  },
  {
    title: "Planning Condition Compliance",
    description: "Monitoring schemes designed to satisfy planning conditions, with reporting formatted for local authority submission and condition discharge.",
    path: "/dust-monitoring-planning-conditions",
  },
  {
    title: "PM10 Monitoring",
    description: "Continuous PM10 measurement — the primary regulatory parameter for construction dust — with IAQM-aligned trigger levels.",
    path: "/pm10-monitoring-construction",
  },
  {
    title: "PM2.5 Monitoring",
    description: "Fine particulate monitoring for sites near sensitive receptors — schools, hospitals, and residential areas where health impact is critical.",
    path: "/pm25-monitoring-construction",
  },
  {
    title: "Demolition Dust Monitoring",
    description: "Specialist monitoring for demolition sites — the highest-risk phase for dust generation on any construction project.",
    path: "/dust-monitoring-demolition-sites",
  },
  {
    title: "Complaint Investigation",
    description: "Rapid deployment to respond to dust complaints and Environmental Health Officer enquiries with objective monitoring data.",
    path: "/dust-monitoring-complaints",
  },
];

const stats = [
  { icon: Building2, value: "500+", label: "Sites Monitored" },
  { icon: Clock, value: "48h", label: "Rapid Deployment" },
  { icon: Shield, value: "100%", label: "Compliance Rate" },
  { icon: Award, value: "MCERTS", label: "Equivalent Equipment" },
];

const triggers = [
  "Planning conditions requiring dust monitoring",
  "Demolition or high-dust construction phases",
  "Dust complaints or EHO involvement",
  "Sensitive receptors nearby — schools, hospitals, residential",
  "Dust Management Plan compliance requirements",
  "Sites within an Air Quality Management Area",
];

const testimonials = [
  {
    quote: "Their dust monitoring gave us the compliance evidence we needed to keep our demolition project on track. Highly professional.",
    author: "Project Manager",
    company: "Tier 1 Contractor, Central London",
  },
  {
    quote: "Rapid deployment and excellent reporting. The real-time PM10 alerts saved us from potential enforcement action.",
    author: "Site Manager",
    company: "Residential Developer, South London",
  },
  {
    quote: "Clear, auditable dust monitoring reports that satisfied our planning conditions without any pushback from the local authority.",
    author: "Environmental Consultant",
    company: "Infrastructure Project, East London",
  },
];

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Construction Dust Monitoring London | PM10 & PM2.5</title>
        <meta name="description" content="Specialist PM10 & PM2.5 boundary dust monitoring for London construction & demolition sites. Planning-compliant, real-time data, 48h deployment." />
        <link rel="canonical" href="https://dustmonitoringlondon.co.uk/" />
        <meta property="og:title" content="Construction Dust Monitoring London | PM10 & PM2.5" />
        <meta property="og:description" content="Specialist PM10 & PM2.5 boundary dust monitoring for London construction & demolition sites. Planning-compliant, real-time data." />
        <meta property="og:url" content="https://dustmonitoringlondon.co.uk/" />
      </Helmet>
      <LocalBusinessSchema />
      <SiteHeader />
      <main>
        {/* Hero */}
        <section className="relative min-h-[90vh] flex items-end overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 hero-overlay" />
          <LeafShape className="absolute top-20 right-[8%] w-32 h-32 text-primary-foreground/[0.04] rotate-45 hidden lg:block" />
          <BlobShape className="absolute bottom-10 left-[5%] w-48 h-48 text-primary-foreground/[0.05] hidden lg:block" />

          <div className="container mx-auto px-4 relative z-10 pb-20 pt-32">
            <div className="grid lg:grid-cols-12 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="lg:col-span-7"
              >
                <div className="flex items-center gap-2 mb-8">
                  <Wind className="w-4 h-4 text-accent" />
                  <span className="text-xs font-medium text-accent tracking-[0.2em] uppercase">
                    Specialist Dust Monitoring
                  </span>
                </div>

                <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-primary-foreground leading-[1.1] mb-8 tracking-tight">
                  Construction Dust
                  <br />
                  Monitoring
                  <br />
                  <span className="italic font-normal">Services London</span>
                </h1>

                <div className="w-16 h-[2px] bg-accent mb-8" />

                <p className="text-lg text-primary-foreground/75 leading-relaxed mb-10 max-w-lg font-light">
                  Professional PM10 and PM2.5 boundary monitoring for construction and demolition projects across London. Planning-compliant real-time data, automated alerts, and compliance reporting for contractors, developers, and demolition companies.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-10">
                  <Link
                    to="/request-quote"
                    className="inline-flex items-center justify-center gap-2 bg-accent text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-terracotta-hover transition-colors"
                  >
                    Request a Quote
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/dust-monitoring-services-london"
                    className="inline-flex items-center justify-center gap-2 border border-primary-foreground/25 text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
                  >
                    Our Services
                  </Link>
                </div>

                {/* Quick-quote inline form */}
                <div className="bg-primary-foreground/10 backdrop-blur-md rounded-xl p-5 border border-primary-foreground/15 max-w-lg">
                  <p className="text-primary-foreground text-sm font-semibold mb-3">Get a Quick Quote</p>
                  <form
                    className="flex flex-col sm:flex-row gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      window.location.href = "/request-quote";
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Your name"
                      className="flex-1 px-4 py-2.5 rounded-lg bg-primary-foreground/10 border border-primary-foreground/15 text-primary-foreground text-sm placeholder:text-primary-foreground/40 focus:outline-none focus:ring-2 focus:ring-accent/50"
                    />
                    <input
                      type="email"
                      placeholder="Email address"
                      className="flex-1 px-4 py-2.5 rounded-lg bg-primary-foreground/10 border border-primary-foreground/15 text-primary-foreground text-sm placeholder:text-primary-foreground/40 focus:outline-none focus:ring-2 focus:ring-accent/50"
                    />
                    <button
                      type="submit"
                      className="bg-accent text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-terracotta-hover transition-colors whitespace-nowrap"
                    >
                      Get Quote
                    </button>
                  </form>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="lg:col-span-5 lg:self-end"
              >
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { num: "32", label: "London Boroughs Covered" },
                    { num: "24/7", label: "Real-Time Monitoring" },
                    { num: "48h", label: "Rapid Deployment" },
                    { num: "100%", label: "Planning Compliance" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-primary-foreground/10 backdrop-blur-sm rounded-lg p-5 border border-primary-foreground/10">
                      <p className="text-2xl font-serif text-primary-foreground">{stat.num}</p>
                      <p className="text-xs text-primary-foreground/60 mt-1 font-medium">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats + Social Proof */}
        <section className="py-20 lg:py-28 bg-primary relative overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
            >
              {stats.map((stat, i) => (
                <div key={stat.label} className="text-center">
                  <div className="w-14 h-14 rounded-full bg-primary-foreground/10 mx-auto mb-4 flex items-center justify-center">
                    <stat.icon className="w-6 h-6 text-accent" />
                  </div>
                  <p className="text-3xl font-serif text-primary-foreground mb-1">{stat.value}</p>
                  <p className="text-xs text-primary-foreground/60 font-medium uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </motion.div>

            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif text-primary-foreground leading-tight tracking-tight">
                Trusted by London's <span className="italic font-normal">Construction Industry</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * i }}
                  className="bg-primary-foreground/5 backdrop-blur-sm rounded-2xl p-7 border border-primary-foreground/10"
                >
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-accent fill-accent" />
                    ))}
                  </div>
                  <p className="text-primary-foreground/80 text-sm leading-relaxed mb-6 italic font-light">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div>
                    <p className="text-primary-foreground text-sm font-semibold">{t.author}</p>
                    <p className="text-primary-foreground/50 text-xs">{t.company}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Dust Risks on Construction Sites */}
        <section className="py-24 lg:py-32">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-12 gap-16">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-5"
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary mb-4 block">
                  Understanding Dust Risks
                </span>
                <h2 className="text-4xl md:text-5xl font-serif leading-[1.15] tracking-tight mb-6">
                  Construction Dust
                  <br />
                  <span className="italic font-normal">Risks</span>
                </h2>
                <div className="w-12 h-[2px] bg-accent mb-6" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-6 lg:col-start-7"
              >
                <div className="space-y-6 text-muted-foreground leading-relaxed font-light">
                  <p>
                    Construction activity is one of the most significant sources of particulate matter in London's urban environment. Demolition, excavation, cutting, crushing, and material handling all generate PM10 and PM2.5 particles that can affect air quality well beyond the site boundary.
                  </p>
                  <p>
                    Without boundary monitoring, there is no objective way to demonstrate that dust emissions remain within acceptable limits. Complaints from neighbours, Environmental Health Officer involvement, and planning condition breaches can all result in costly project delays and enforcement action.
                  </p>
                  <p>
                    Professional dust monitoring provides the real-time data and documented evidence needed to manage these risks — protecting both your project and the communities around your site.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-24 lg:py-32 section-forest-light">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary mb-4 block">
                Our Services
              </span>
              <h2 className="text-4xl md:text-5xl font-serif leading-[1.15] tracking-tight">
                Dust Monitoring <span className="italic font-normal">Services</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, i) => (
                <motion.div
                  key={service.path}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                >
                  <Link
                    to={service.path}
                    className="block bg-card rounded-2xl p-8 border border-border group hover:border-primary/30 transition-all duration-300 h-full"
                  >
                    <h3 className="text-xl font-serif mb-3 group-hover:text-primary transition-colors">{service.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{service.description}</p>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Learn more <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* When Monitoring Is Required */}
        <section className="py-24 lg:py-32">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-12 gap-16">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-5"
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary mb-4 block">
                  When Is Monitoring Required?
                </span>
                <h2 className="text-4xl md:text-5xl font-serif leading-[1.15] tracking-tight mb-6">
                  Common
                  <br />
                  <span className="italic font-normal">Triggers</span>
                </h2>
                <div className="w-12 h-[2px] bg-accent mb-6" />
                <p className="text-muted-foreground leading-relaxed font-light">
                  Construction dust monitoring is typically triggered by regulatory, planning, or project-specific requirements across London.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:col-span-6 lg:col-start-7"
              >
                <ul className="space-y-5">
                  {triggers.map((trigger, i) => (
                    <li key={i} className="flex items-start gap-4 group">
                      <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground leading-relaxed border-b border-border pb-4 flex-1">{trigger}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Real-Time Monitoring Explanation */}
        <section className="py-24 lg:py-32 section-forest-light">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-12 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-6"
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary mb-4 block">
                  Real-Time Data
                </span>
                <h2 className="text-4xl md:text-5xl font-serif leading-[1.15] tracking-tight mb-6">
                  Live Dust <span className="italic font-normal">Monitoring</span>
                </h2>
                <div className="w-12 h-[2px] bg-accent mb-6" />
                <p className="text-muted-foreground leading-relaxed font-light mb-6">
                  Our real-time dust monitoring system provides continuous PM10 and PM2.5 data from site boundaries, accessible 24/7 through secure cloud dashboards. Automated alerts notify site teams when concentrations approach action levels — enabling rapid mitigation before exceedances become enforcement issues.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-card rounded-xl p-4 border border-border">
                    <Activity className="w-5 h-5 text-primary mb-2" />
                    <p className="text-sm font-semibold">PM10 Monitoring</p>
                    <p className="text-xs text-muted-foreground mt-1">Primary construction dust parameter</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-border">
                    <Activity className="w-5 h-5 text-accent mb-2" />
                    <p className="text-sm font-semibold">PM2.5 Monitoring</p>
                    <p className="text-xs text-muted-foreground mt-1">Fine particles near sensitive receptors</p>
                  </div>
                </div>
                <Link
                  to="/real-time-dust-monitoring"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  Learn about real-time monitoring <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:col-span-5 lg:col-start-8"
              >
                <div className="bg-card rounded-2xl p-8 border border-border space-y-4">
                  {[
                    "15-minute PM10 & PM2.5 readings",
                    "Automated SMS & email alerts",
                    "Cloud dashboard — 24/7 access",
                    "Wind direction correlation",
                    "Multi-site map overview",
                    "Exportable compliance reports",
                  ].map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 lg:py-32">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary mb-4 block">
                Simple Process
              </span>
              <h2 className="text-4xl md:text-5xl font-serif leading-[1.15] tracking-tight">
                How It <span className="italic font-normal">Works</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {[
                {
                  icon: ClipboardCheck,
                  num: "01",
                  title: "Scope & Quote",
                  description: "Tell us about your project. We'll assess your planning conditions and dust monitoring requirements, then provide a clear, fixed-price quote.",
                },
                {
                  icon: Truck,
                  num: "02",
                  title: "Deploy in 48h",
                  description: "Our team installs MCERTS-equivalent PM10 and PM2.5 monitoring equipment at your site boundary — fully operational within 48 hours.",
                },
                {
                  icon: BarChart3,
                  num: "03",
                  title: "Monitor & Report",
                  description: "Access real-time dust data via your dashboard. Receive automated alerts and planning-compliant reports ready for local authority submission.",
                },
              ].map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="relative text-center group"
                >
                  {i < 2 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] border-t border-dashed border-border" />
                  )}
                  <div className="relative z-10">
                    <div className="w-24 h-24 rounded-full bg-forest-light mx-auto mb-6 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <step.icon className="w-10 h-10 text-primary" />
                    </div>
                    <span className="text-xs font-semibold text-accent tracking-wider mb-2 block">
                      STEP {step.num}
                    </span>
                    <h3 className="text-xl font-serif mb-3">{step.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-14">
              <Link
                to="/request-quote"
                className="inline-flex items-center justify-center gap-2 bg-accent text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-terracotta-hover transition-colors"
              >
                Get Your Free Quote
              </Link>
            </div>
          </div>
        </section>

        {/* Target Audience / Case Examples */}
        <section className="py-24 lg:py-32 section-forest-light">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-12 gap-16">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-5"
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary mb-4 block">
                  Who We Work With
                </span>
                <h2 className="text-4xl md:text-5xl font-serif leading-[1.15] tracking-tight mb-6">
                  Built for <span className="italic font-normal">Construction</span>
                </h2>
                <div className="w-12 h-[2px] bg-accent mb-6" />
                <p className="text-muted-foreground leading-relaxed font-light">
                  Our dust monitoring service is used by contractors, developers, and demolition companies across London — from single-phase demolition projects to multi-year development programmes.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-6 lg:col-start-7 space-y-6"
              >
                {[
                  {
                    title: "Demolition Project — Central London",
                    text: "PM10 and PM2.5 boundary monitoring throughout a 6-month demolition phase adjacent to residential properties. Real-time alerts enabled the contractor to adjust water suppression in response to rising dust levels, avoiding planning condition breaches.",
                  },
                  {
                    title: "Residential Development — South London",
                    text: "Continuous dust monitoring across excavation and superstructure phases to satisfy planning conditions requiring monthly compliance reports. Data was shared with the local authority via dashboard access.",
                  },
                  {
                    title: "Complaint Response — East London",
                    text: "Rapid deployment of PM10 monitors within 24 hours of an EHO complaint. Monitoring data demonstrated that dust levels at the boundary were within IAQM trigger levels, resolving the complaint without enforcement action.",
                  },
                ].map((example, i) => (
                  <div key={i} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="font-serif text-lg mb-2">{example.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{example.text}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-primary relative overflow-hidden">
          <BlobShape className="absolute -top-20 -right-16 w-80 h-80 text-forest-dark/30" />
          <BlobShape className="absolute -bottom-24 -left-20 w-72 h-72 text-forest-dark/20 rotate-180" />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 className="text-3xl md:text-4xl font-serif text-primary-foreground leading-tight tracking-tight mb-4">
                Need Dust Monitoring for a London Construction Project?
              </h2>
              <p className="text-primary-foreground/60 mb-8 font-light max-w-xl mx-auto">
                Planning-compliant PM10 and PM2.5 boundary monitoring — deployed across London within 48 hours. Free, no-obligation quotation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/request-quote"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-terracotta-hover transition-colors"
                >
                  Request a Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:08004337914"
                  className="inline-flex items-center justify-center gap-2 border border-primary-foreground/25 text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
                >
                  Call 0800 433 7914
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <StickyCtaBar />
    </div>
  );
};

export default Index;
